import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../config/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * ContactForm
 * - Anti-spam: server challenge token + minimum submit time, honeypot, rate-limit on server.
 * - UI: disables submit until minSubmitMs passed.
 */
export default function ContactForm({ pageSlug = null, title = 'Контактный формуляр' }) {
    const { user } = useAuth();

    const [loadingChallenge, setLoadingChallenge] = useState(true);
    const [challengeToken, setChallengeToken] = useState('');
    const [minSubmitMs, setMinSubmitMs] = useState(5000);
    const [challengeAt, setChallengeAt] = useState(0);
    const [nowMs, setNowMs] = useState(() => Date.now());

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [policyAccepted, setPolicyAccepted] = useState(false);
    const [company, setCompany] = useState(''); // honeypot (must stay empty)

    const [sending, setSending] = useState(false);
    const mountedRef = useRef(false);

    // Prefill from auth user (can remain editable)
    useEffect(() => {
        if (!user) return;
        // Only prefill when fields are empty to avoid overriding user input.
        setName((prev) => (prev ? prev : String(user.username || user.name || '').trim()));
        setEmail((prev) => (prev ? prev : String(user.email || '').trim()));
    }, [user]);

    // Fetch challenge
    useEffect(() => {
        mountedRef.current = true;
        const run = async () => {
            setLoadingChallenge(true);
            try {
                const res = await api.get('/api/contact/challenge');
                const token = res?.data?.token;
                const ms = Number(res?.data?.minSubmitMs || 5000);
                if (!token) throw new Error('No challenge token');
                if (!mountedRef.current) return;
                setChallengeToken(String(token));
                setMinSubmitMs(Number.isFinite(ms) && ms > 0 ? ms : 5000);
                const t0 = Date.now();
                setChallengeAt(t0);
                setNowMs(t0);
            } catch (e) {
                const msg = e?.response?.data?.error || e?.message || 'Не удалось загрузить защиту формы';
                toast.error(msg);
            } finally {
                if (mountedRef.current) setLoadingChallenge(false);
            }
        };
        run();
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const waitLeftMs = useMemo(() => {
        if (!challengeAt) return minSubmitMs;
        return Math.max(0, minSubmitMs - (nowMs - challengeAt));
    }, [challengeAt, minSubmitMs, nowMs]);

    // Tick a lightweight clock while waiting so UI updates reliably.
    useEffect(() => {
        if (!challengeAt) return;
        if (waitLeftMs <= 0) return;
        const id = setInterval(() => {
            setNowMs(Date.now());
        }, 250);
        return () => clearInterval(id);
    }, [challengeAt, waitLeftMs]);

    const canSubmit = !loadingChallenge && !sending && !!challengeToken && waitLeftMs <= 0;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!challengeToken) {
            toast.error('Защита формы не готова. Обновите страницу.');
            return;
        }
        if (!policyAccepted) {
            toast.error('Подтвердите согласие с политикой конфиденциальности');
            return;
        }
        if (waitLeftMs > 0) {
            toast.error(`Пожалуйста, подождите ещё ${Math.ceil(waitLeftMs / 1000)} сек.`);
            return;
        }

        setSending(true);
        try {
            const payload = {
                name: String(name || '').trim(),
                email: String(email || '').trim(),
                message: String(message || '').trim(),
                // Backend expects string 'true' currently.
                policyAccepted: policyAccepted ? 'true' : 'false',
                challengeToken,
                company: String(company || ''),
                pageSlug: pageSlug ? String(pageSlug) : null
            };
            const res = await api.post('/api/contact', payload);
            if (res?.data?.success) {
                toast.success('Сообщение отправлено');
                setMessage('');
                setPolicyAccepted(false);
                setCompany('');
                // refresh challenge so repeated submits still require waiting
                try {
                    const ch = await api.get('/api/contact/challenge');
                    setChallengeToken(String(ch?.data?.token || ''));
                    setMinSubmitMs(Number(ch?.data?.minSubmitMs || 5000));
                    const t1 = Date.now();
                    setChallengeAt(t1);
                    setNowMs(t1);
                } catch (_) {
                    // no hard failure; user can reload
                }
            } else {
                toast.error('Не удалось отправить сообщение');
            }
        } catch (e2) {
            const code = e2?.response?.data?.code;
            const msg = e2?.response?.data?.error || e2?.message || 'Ошибка отправки';
            if (code === 'TOO_FAST') {
                toast.error('Слишком быстро. Подождите несколько секунд и попробуйте снова.');
            } else if (code === 'CHALLENGE_EXPIRED') {
                toast.error('Сессия формы истекла. Обновите страницу.');
            } else if (e2?.response?.status === 429) {
                toast.error('Слишком много запросов. Попробуйте позже.');
            } else {
                toast.error(msg);
            }
        } finally {
            setSending(false);
        }
    };

    return (
        <section className="mt-10 border-t border-secondary-200 pt-8">
            <h2 className="text-2xl font-bold text-secondary-900 mb-4">{title}</h2>
            <p className="text-secondary-600 mb-6 text-sm">
                {loadingChallenge
                    ? 'Загрузка защиты формы…'
                    : waitLeftMs > 0
                        ? `Пожалуйста, подождите ${Math.ceil(waitLeftMs / 1000)} сек. перед отправкой.`
                        : 'Заполните форму ниже.'}
            </p>

            <form onSubmit={onSubmit} className="max-w-2xl space-y-4">
                {/* Honeypot (hidden) */}
                <div style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
                    <label>
                        Company
                        <input
                            type="text"
                            tabIndex={-1}
                            autoComplete="off"
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                        />
                    </label>
                </div>

                <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Ваше имя*</label>
                    <input
                        className="input w-full"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Иван Иванов"
                        required
                        maxLength={120}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Ваш email адрес*</label>
                    <input
                        type="email"
                        className="input w-full"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="name@example.com"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Сообщение*</label>
                    <textarea
                        className="input w-full min-h-[140px]"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Напишите ваше сообщение…"
                        required
                        maxLength={5000}
                    />
                    <div className="mt-1 text-xs text-secondary-500">{(message || '').length}/5000</div>
                </div>

                <label className="flex items-start gap-3">
                    <input
                        type="checkbox"
                        className="mt-1 h-4 w-4"
                        checked={policyAccepted}
                        onChange={(e) => setPolicyAccepted(e.target.checked)}
                    />
                    <span className="text-sm text-secondary-800">
                        С политикой конфиденциальности и соглашением ознакомлен
                    </span>
                </label>

                <div className="flex items-center gap-3">
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={!canSubmit}
                    >
                        {sending ? 'Отправка…' : 'Отправить'}
                    </button>
                    {!loadingChallenge && waitLeftMs > 0 && (
                        <span className="text-xs text-secondary-600">Ждите…</span>
                    )}
                </div>

                {!!user && (
                    <div className="text-xs text-secondary-500">
                        Вы авторизованы как <b>{user.username || user.email}</b>. Имя и email могут быть отредактированы, но на сервере будут проверены.
                    </div>
                )}
            </form>
        </section>
    );
}
